'use client';

import { cn } from '@zealot/ui/lib/utils';
import { Check, ChevronDown, GripVertical, Lock, MoreHorizontal, Pencil, Plus, Trash2, Users } from 'lucide-react';
import type { CSSProperties } from 'react';
import * as React from 'react';
import { computeVisibleViews, mergeVisibleReorder } from '../../lib/filtering/savedViewOverflow';
import { SortableList } from '../../sortable/SortableList';
import { useSortableItem } from '../../sortable/useSortableItem';
import { Badge } from '../badge';
import { Button } from '../button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../command';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../dropdown-menu';
import { Input } from '../input';
import { Popover, PopoverContent, PopoverTrigger } from '../popover';
import { Tabs, TabsList, TabsTrigger, tabsTriggerVariants } from '../tabs';

// gloss
const TAB_GAP = 8;
// gloss
const TABS_LIST_PADDING = 8;
// gloss
const MORE_BUTTON_WIDTH_ESTIMATE = 130;

// gloss
type SavedView = {
  uuid: string;
  name: string;
  isShared: boolean;
  isOwner?: boolean;
};

// gloss
type SavedViewTabItemProps = {
  view: SavedView;
  onRename: (uuid: string, name: string) => void;
  onToggleShared: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  dragRef?: (node: HTMLElement | null) => void;
  dragStyle?: CSSProperties;
  dragListeners?: Record<string, unknown>;
  isDragging?: boolean;
};

// gloss
const SavedViewTabItem = ({
  view,
  onRename,
  onToggleShared,
  onDelete,
  dragRef,
  dragStyle,
  dragListeners,
  isDragging,
}: SavedViewTabItemProps) => {
  const [isEditing, setIsEditing] = React.useState(false);
  const [editName, setEditName] = React.useState(view.name);
  const canManage = view.isOwner !== false;

  const commitRename = () => {
    const trimmed = editName.trim();
    if (trimmed && trimmed !== view.name) onRename(view.uuid, trimmed);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <Input
        autoFocus
        size="sm"
        aria-label={`Rename ${view.name}`}
        value={editName}
        onChange={(event) => setEditName(event.target.value)}
        onBlur={commitRename}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commitRename();
          if (event.key === 'Escape') {
            setEditName(view.name);
            setIsEditing(false);
          }
        }}
        maxLength={100}
        className="h-9 w-32"
      />
    );
  }

  return (
    <div
      ref={dragRef}
      style={dragStyle}
      {...(dragListeners ?? {})}
      className={cn('group relative', dragListeners && 'cursor-grab touch-none', isDragging && 'opacity-60')}
    >
      <TabsTrigger value={view.uuid} title={view.name} className={cn(canManage && 'pr-8')}>
        {}
        {dragListeners && (
          <GripVertical
            aria-hidden
            className="-translate-y-1/2 absolute top-1/2 left-1 size-3.5 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground"
          />
        )}
        {view.isShared ? <Users /> : <Lock />}
        {}
        <span className="max-w-[12rem] truncate">{view.name}</span>
      </TabsTrigger>
      {canManage && (
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <button
                type="button"
                aria-label={`${view.name} view options`}
                className="absolute top-1/2 right-1 -translate-y-1/2 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted group-focus-within:opacity-100 group-hover:opacity-100"
              />
            }
          >
            <MoreHorizontal className="size-3.5" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={() => {
                setEditName(view.name);
                setIsEditing(true);
              }}
            >
              <Pencil />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => onToggleShared(view.uuid)}>
              {view.isShared ? <Lock /> : <Users />}
              {view.isShared ? 'Make private' : 'Make shared'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onClick={() => onDelete(view.uuid)}>
              <Trash2 />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};

// gloss
const SortableSavedViewTabItem = (props: SavedViewTabItemProps) => {
  const { setNodeRef, style, listeners, isDragging } = useSortableItem(props.view.uuid);
  return (
    <SavedViewTabItem
      {...props}
      dragRef={setNodeRef}
      dragStyle={style}
      dragListeners={listeners}
      isDragging={isDragging}
    />
  );
};

// gloss
const ALL_TAB_VALUE = '__all__';

// gloss
type SavedViewTabsProps = {
  views: SavedView[];
  activeViewUuid: string | null;
  onSelect: (uuid: string) => void;
  onRename: (uuid: string, name: string) => void;
  onToggleShared: (uuid: string) => void;
  onDelete: (uuid: string) => void;
  onCreateNew: () => void;
  onSelectAll?: () => void;
  onReorder?: (orderedUuids: string[]) => void;
  allLabel?: string;
  className?: string;
};

// gloss
const SavedViewTabs = ({
  views,
  activeViewUuid,
  onSelect,
  onRename,
  onToggleShared,
  onDelete,
  onCreateNew,
  onSelectAll,
  onReorder,
  allLabel = 'All',
  className,
}: SavedViewTabsProps) => {
  const activeValue = activeViewUuid ?? (onSelectAll ? ALL_TAB_VALUE : null);

  const handleValueChange = (value: string) => {
    if (onSelectAll && value === ALL_TAB_VALUE) {
      onSelectAll();
      return;
    }
    onSelect(value);
  };

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(0);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width !== undefined) setContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const allTabRef = React.useRef<HTMLButtonElement>(null);
  const newViewButtonRef = React.useRef<HTMLButtonElement>(null);
  const [reservedWidth, setReservedWidth] = React.useState(0);

  React.useEffect(() => {
    const allWidth = allTabRef.current?.getBoundingClientRect().width ?? 0;
    const newViewWidth = newViewButtonRef.current?.getBoundingClientRect().width ?? 0;
    setReservedWidth(allWidth + newViewWidth + (allWidth > 0 ? TAB_GAP : 0) + TAB_GAP + TABS_LIST_PADDING);
  }, [onSelectAll, allLabel]);

  const measureRefs = React.useRef(new Map<string, HTMLDivElement | null>());
  const [tabWidths, setTabWidths] = React.useState<Record<string, number>>({});

  React.useEffect(() => {
    const next: Record<string, number> = {};
    for (const view of views) {
      const width = measureRefs.current.get(view.uuid)?.getBoundingClientRect().width;
      if (width !== undefined) next[view.uuid] = width;
    }
    setTabWidths(next);
  }, [views]);

  const moreButtonRef = React.useRef<HTMLButtonElement>(null);
  const [moreButtonWidth, setMoreButtonWidth] = React.useState(MORE_BUTTON_WIDTH_ESTIMATE);

  const { visible, overflow } = computeVisibleViews({
    views,
    activeUuid: activeViewUuid,
    containerWidth,
    tabWidths,
    reservedWidth,
    moreButtonWidth,
  });

  const handleReorder = (nextVisibleUuids: string[]) => {
    onReorder?.(
      mergeVisibleReorder(
        views.map((view) => view.uuid),
        nextVisibleUuids,
      ),
    );
  };
  const isReorderable = Boolean(onReorder);

  const [overflowOpen, setOverflowOpen] = React.useState(false);

  React.useEffect(() => {
    if (overflow.length === 0) {
      setOverflowOpen(false);
      return;
    }
    const width = moreButtonRef.current?.getBoundingClientRect().width;
    if (width) setMoreButtonWidth((prev) => (Math.abs(width - prev) > 1 ? width : prev));
  }, [overflow.length]);

  return (
    <Tabs
      data-slot="saved-view-tabs"
      value={activeValue}
      onValueChange={handleValueChange}
      className={cn('w-full min-w-0', className)}
    >
      <div ref={containerRef} data-slot="saved-view-tabs-container" className="relative w-full min-w-0 overflow-hidden">
        {}
        <div className="flex items-center gap-2 border-border border-b">
          <TabsList variant="card" size="lg">
            {onSelectAll && (
              <TabsTrigger ref={allTabRef} value={ALL_TAB_VALUE}>
                {allLabel}
              </TabsTrigger>
            )}
            {isReorderable ? (
              <SortableList ids={visible.map((view) => view.uuid)} orientation="horizontal" onReorder={handleReorder}>
                {visible.map((view) => (
                  <SortableSavedViewTabItem
                    key={view.uuid}
                    view={view}
                    onRename={onRename}
                    onToggleShared={onToggleShared}
                    onDelete={onDelete}
                  />
                ))}
              </SortableList>
            ) : (
              visible.map((view) => (
                <SavedViewTabItem
                  key={view.uuid}
                  view={view}
                  onRename={onRename}
                  onToggleShared={onToggleShared}
                  onDelete={onDelete}
                />
              ))
            )}
          </TabsList>
          {overflow.length > 0 && (
            <Popover open={overflowOpen} onOpenChange={setOverflowOpen}>
              <PopoverTrigger
                render={
                  <button
                    ref={moreButtonRef}
                    type="button"
                    className={cn(
                      tabsTriggerVariants({ variant: 'card', size: 'lg' }),
                      'flex-none hover:bg-primary/10 hover:text-primary',
                      'data-popup-open:border-t-2 data-popup-open:border-t-primary data-popup-open:border-r-0 data-popup-open:border-b-0 data-popup-open:border-l-0 data-popup-open:bg-background data-popup-open:text-primary',
                    )}
                  />
                }
              >
                More
                <Badge variant="default">{overflow.length}</Badge>
                <ChevronDown />
              </PopoverTrigger>
              {}
              <PopoverContent align="end" className="w-64 p-0">
                <Command>
                  <CommandInput placeholder="Search views..." />
                  <CommandList>
                    <CommandEmpty>No views found</CommandEmpty>
                    <CommandGroup heading="Saved views">
                      {overflow.map((view) => (
                        <CommandItem
                          key={view.uuid}
                          value={view.name}
                          onSelect={() => {
                            onSelect(view.uuid);
                            setOverflowOpen(false);
                          }}
                          className="data-selected:bg-primary/10 data-selected:text-primary"
                        >
                          {view.isShared ? (
                            <Users className="text-muted-foreground" />
                          ) : (
                            <Lock className="text-muted-foreground" />
                          )}
                          <span className="flex-1 truncate">{view.name}</span>
                          {view.uuid === activeViewUuid && <Check className="ml-auto text-primary" />}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                    <CommandSeparator />
                    <CommandGroup>
                      <CommandItem
                        value="__new_view__"
                        onSelect={() => {
                          onCreateNew();
                          setOverflowOpen(false);
                        }}
                        className="text-primary data-selected:bg-primary/10 data-selected:text-primary"
                      >
                        <Plus />
                        New view
                      </CommandItem>
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          )}
          <Button ref={newViewButtonRef} variant="ghost" size="icon-sm" aria-label="New view" onClick={onCreateNew}>
            <Plus />
          </Button>
        </div>

        {}
        <div aria-hidden data-slot="saved-view-tabs-measurer" className="invisible absolute top-0 left-0 flex gap-2">
          {views.map((view) => (
            <div
              key={view.uuid}
              data-view-uuid={view.uuid}
              ref={(el) => {
                measureRefs.current.set(view.uuid, el);
              }}
              className={cn(tabsTriggerVariants({ variant: 'card', size: 'lg' }), view.isOwner !== false && 'pr-8')}
            >
              {view.isShared ? <Users /> : <Lock />}
              {}
              <span className="max-w-[12rem] truncate">{view.name}</span>
            </div>
          ))}
        </div>
      </div>
    </Tabs>
  );
};

export type { SavedView, SavedViewTabsProps };
export { SavedViewTabs };
